/**
 * EtRekenmachine — de examenrekenmachine voor de examentrainingsmodule.
 *
 * Functioneel gespiegeld aan de rekenmachine die leerlingen van hun digitale
 * examen (Facet) kennen: zelfde soort knoppenrooster, komma als decimaalteken,
 * graden voor sin/cos/tan, INV, ANS, f↔d (breuk↔decimaal) en — het
 * belangrijkste — "Zet in antwoord": de uitgevoerde berekening wordt als regel
 * in het antwoordveld geplakt, zodat de leerling laat zien hoe hij rekent.
 * Vormgeving en teksten zijn van ons (tw-tokens), het gedrag is vertrouwd.
 *
 * Rekenlogica zit in src/utils/etRekenmachine.js (pure module, 27 tests).
 *
 * Ook in een gewone toets/communicatiesessie (01-11-2026, ToetsHulpmiddelen):
 *  - variant 'eenvoudig' laat de wetenschappelijke rijen weg (wel haakjes en C);
 *  - zonder onPlak geen knop "Zet in antwoord";
 *  - gedockt: het paneel staat in de zijkolom naast de vraag i.p.v. zwevend;
 *  - toetsenbord: cijfers, + - * / ^ ( ) , . Enter/= Backspace Delete, Esc sluit.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { reken, toonGetal, alsBreuk } from '../../utils/etRekenmachine';
import './EtRekenmachine.css';

export default function EtRekenmachine({ open, onSluit, onPlak, variant = 'wetenschappelijk', gedockt = false, autoFocus = false }) {
  const [invoer, setInvoer] = useState('');
  const [uitkomst, setUitkomst] = useState(null);   // {expr, waarde} na "="
  const [ans, setAns] = useState(0);
  const [inv, setInv] = useState(false);
  const [breukWeergave, setBreukWeergave] = useState(false);
  const [fout, setFout] = useState(null);
  const [pos, setPos] = useState({ x: null, y: null });
  const sleepRef = useRef(null);
  const wetenschappelijk = variant !== 'eenvoudig';

  // ── slepen aan de titelbalk ──
  const startSleep = useCallback((e) => {
    const paneel = sleepRef.current;
    if (!paneel) return;
    if (window.innerWidth <= 480) return;   // review-fix #3: mobiel niet slepen, layout blijft heel
    const rect = paneel.getBoundingClientRect();
    const dx = e.clientX - rect.left, dy = e.clientY - rect.top;
    const pid = e.pointerId;
    const move = (ev) => {
      if (ev.pointerId !== pid) return;      // tweede vinger laat het paneel niet springen
      // clamp: de titelbalk blijft altijd pakbaar binnen het scherm
      const x = Math.min(Math.max(ev.clientX - dx, 8 - rect.width + 80), window.innerWidth - 80);
      const y = Math.min(Math.max(ev.clientY - dy, 8), window.innerHeight - 60);
      setPos({ x, y });
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);  // review-fix #3: ook bij cancel loslaten
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  }, []);

  // Review-fix #3: bij sluiten de sleep-positie vergeten, zodat heropenen
  // altijd op de standaardplek gebeurt (nooit buiten beeld).
  useEffect(() => { if (!open) setPos({ x: null, y: null }); }, [open]);

  // Toetsenbord: bij openen de focus op het paneel, zodat typen meteen rekent.
  useEffect(() => {
    if (open && autoFocus && sleepRef.current) sleepRef.current.focus();
  }, [open, autoFocus]);

  if (!open) return null;

  const tik = (t) => {
    setFout(null);
    if (uitkomst) {
      // Review-fix #2: na '=' gedraagt hij zich als de rekenmachine die
      // leerlingen kennen — een operator rekent door op ANS, al het andere
      // begint een verse berekening.
      const isOperator = /^[+\-−×÷*/^]/.test(t);
      setInvoer(isOperator ? `ans${t}` : t);
    } else {
      setInvoer((s) => s + t);
    }
    setUitkomst(null);
  };
  const wis = () => { setInvoer(''); setUitkomst(null); setFout(null); };
  const del = () => { setFout(null); setUitkomst(null); setInvoer((s) => s.slice(0, -1)); };

  const isGelijk = () => {
    try {
      const waarde = reken(invoer, ans);
      setUitkomst({ expr: invoer, waarde });
      setAns(waarde);
      setFout(null);
    } catch (e) { setFout(e.message); setUitkomst(null); }
  };

  const plak = () => {
    if (!uitkomst || !onPlak) return;
    // Review-fix #4: plak wat het display toont — ook in breukweergave.
    const breuk = breukWeergave ? alsBreuk(uitkomst.waarde) : null;
    const weergave = breuk && breuk.noemer !== 1
      ? `${breuk.teller}/${breuk.noemer}` : toonGetal(uitkomst.waarde);
    onPlak(`${uitkomst.expr} = ${weergave}`, weergave);
    if (onSluit) onSluit();   // paneel weg → de leerling ziet het resultaat landen
  };

  // Toetsenbord (alleen als de focus in het paneel staat).
  const TOETS_NAAR_TEKEN = {
    '*': '×', 'x': '×', '/': '÷', ':': '÷', '.': ',', ',': ',',
    '+': '+', '-': '-', '(': '(', ')': ')',
  };
  const opToets = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const k = e.key;
    if (k === 'Escape') {
      if (onSluit) { e.preventDefault(); e.stopPropagation(); onSluit(); }
      return;
    }
    // Enter of spatie op een knop: die knop doet het werk (anders telt hij dubbel).
    if ((k === 'Enter' || k === ' ') && e.target && e.target.tagName === 'BUTTON') return;
    let gedaan = true;
    if (/^[0-9]$/.test(k)) tik(k);
    else if (TOETS_NAAR_TEKEN[k]) tik(TOETS_NAAR_TEKEN[k]);
    else if (k === '^' && wetenschappelijk) tik('^');
    else if (k === 'Enter' || k === '=') isGelijk();
    else if (k === 'Backspace') del();
    else if (k === 'Delete') wis();
    else gedaan = false;
    if (gedaan) { e.preventDefault(); e.stopPropagation(); }
  };

  // display-inhoud
  let groot = invoer || '0';
  if (uitkomst) {
    const breuk = breukWeergave ? alsBreuk(uitkomst.waarde) : null;
    groot = breuk && breuk.noemer !== 1
      ? `${breuk.teller}/${breuk.noemer}`
      : toonGetal(uitkomst.waarde);
  }

  // Gesleept = zwevend (ook als hij gedockt begon).
  const stijl = pos.x === null ? {} : { position: 'fixed', left: pos.x, top: pos.y, right: 'auto' };

  return (
    <div ref={sleepRef} className={`etr-paneel${gedockt ? ' etr-paneel--gedockt' : ''}`} style={stijl}
      role="dialog" aria-label="Rekenmachine" tabIndex={-1} onKeyDown={opToets}
      data-testid="rekenmachine">
      <div className="etr-kop" onPointerDown={startSleep}>
        <span>Rekenmachine</span>
        {/* Geen kruisje: dat teken staat óók op de keer-toets, en een leerling
            die wil vermenigvuldigen klikte de rekenmachine per ongeluk weg.
            Sluiten gaat met dezelfde knop in de werkbalk, net als bij de
            meetlat en de geodriehoek. */}
      </div>

      <div className="etr-display" aria-live="polite">
        <div className="etr-display-expr">{uitkomst ? `${uitkomst.expr} =` : ' '}</div>
        <div className={`etr-display-groot ${fout ? 'fout' : ''}`}>{fout || groot}</div>
      </div>

      {wetenschappelijk ? (<>
      <div className="etr-rij etr-wetenschappelijk">
        <button onClick={() => tik(inv ? 'asin(' : 'sin(')}>{inv ? 'sin⁻¹' : 'sin'}</button>
        <button onClick={() => tik(inv ? 'acos(' : 'cos(')}>{inv ? 'cos⁻¹' : 'cos'}</button>
        <button onClick={() => tik(inv ? 'atan(' : 'tan(')}>{inv ? 'tan⁻¹' : 'tan'}</button>
        <button onClick={() => tik('sqrt(')}>√</button>
        <button onClick={() => tik('*10^')}>×10<sup>y</sup></button>
      </div>
      <div className="etr-rij etr-wetenschappelijk">
        <button onClick={() => tik('^2')}>x²</button>
        <button onClick={() => tik('^')}>x<sup>y</sup></button>
        <button onClick={() => tik('^-1')}>x⁻¹</button>
        <button onClick={() => tik('pi')}>π</button>
        <button className={inv ? 'actief' : ''} onClick={() => setInv((v) => !v)}>INV</button>
      </div>
      <div className="etr-rij etr-wetenschappelijk">
        <button onClick={() => tik('(')}>(</button>
        <button onClick={() => tik(')')}>)</button>
        <button className={breukWeergave ? 'actief' : ''} onClick={() => setBreukWeergave((v) => !v)}
          aria-label="Wissel breuk en decimaal">f↔d</button>
        <button onClick={() => tik('ans')}>ANS</button>
        <button className="etr-c" onClick={wis} aria-label="Alles wissen">C</button>
      </div>
      </>) : (
        <div className="etr-rij etr-rij-eenvoudig">
          <button onClick={() => tik('(')}>(</button>
          <button onClick={() => tik(')')}>)</button>
          <button className="etr-c" onClick={wis} aria-label="Alles wissen">C</button>
        </div>
      )}

      <div className="etr-rooster">
        <button onClick={() => tik('7')}>7</button>
        <button onClick={() => tik('8')}>8</button>
        <button onClick={() => tik('9')}>9</button>
        <button className="etr-del" onClick={del} aria-label="Laatste teken wissen">DEL</button>
        <button onClick={() => tik('4')}>4</button>
        <button onClick={() => tik('5')}>5</button>
        <button onClick={() => tik('6')}>6</button>
        <button className="etr-op" onClick={() => tik('×')}>×</button>
        <button onClick={() => tik('1')}>1</button>
        <button onClick={() => tik('2')}>2</button>
        <button onClick={() => tik('3')}>3</button>
        <button className="etr-op" onClick={() => tik('÷')}>÷</button>
        <button onClick={() => tik('0')}>0</button>
        <button onClick={() => tik(',')}>,</button>
        <button className="etr-op" onClick={() => tik('+')}>+</button>
        <button className="etr-op" onClick={() => tik('-')}>−</button>
      </div>

      <div className={`etr-onder${onPlak ? '' : ' etr-onder--alleen-is'}`}>
        <button className="etr-is" onClick={isGelijk} aria-label="Reken uit">=</button>
        {onPlak && (
          <button className="etr-plak" onClick={plak} disabled={!uitkomst}
            aria-label="Zet de berekening in je antwoord">Zet in antwoord ↴</button>
        )}
      </div>
    </div>
  );
}
